import React, { useEffect, useState } from "react";
import { Trash2, Edit3, Image as ImageIcon, Calendar, FileText } from "lucide-react";

const formatDate = (dateStr) => {
  if (!dateStr) return "—";
  const d = new Date(dateStr);
  return d.toLocaleDateString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
};

export default function ProjectList({
  projects = [],
  onDeleteProject,
  onSelectDraft,
  onSelectProject,
  selectedProjectId,
}) {
  const [drafts, setDrafts] = useState([]);

  useEffect(() => {
    const saved = JSON.parse(localStorage.getItem("project-drafts") || "[]");
    setDrafts(saved);
  }, []);

  const handleDeleteDraft = (id) => {
    if (window.confirm("Delete this draft?")) {
      const updated = drafts.filter((d) => d.id !== id);
      setDrafts(updated);
      localStorage.setItem("project-drafts", JSON.stringify(updated));
    }
  };

  return (
    <div className="w-full max-w-6xl mx-auto p-1 text-xs flex flex-col gap-2 max-h-[580px] overflow-y-auto pr-1">
      {/* Table Header */}
      <div className="grid grid-cols-12 gap-3 px-3 py-2 font-bold bg-gray-100 dark:bg-gray-800 rounded-lg text-gray-700 dark:text-gray-200 text-xs uppercase tracking-wider sticky top-0 z-10 shadow-sm">
        <div className="col-span-1 text-center">#</div>
        <div className="col-span-5">Artwork / Exhibition</div>
        <div className="col-span-2">Type</div>
        <div className="col-span-2">Date</div>
        <div className="col-span-2 text-center">Actions</div>
      </div>

      {/* Drafts section */}
      {drafts.map((d, i) => {
        const imgMatch = d.description?.match(/<img[^>]+src="([^">]+)"/);
        const thumb =
          d.coverImage ||
          (Array.isArray(d.images) && d.images.length > 0
            ? d.images[0]
            : imgMatch
              ? imgMatch[1]
              : null);

        return (
          <div
            key={d.id}
            onClick={() => onSelectDraft?.(d)}
            className="grid grid-cols-12 gap-3 p-2.5 items-center cursor-pointer rounded-xl text-xs transition-all border border-amber-200 bg-amber-50/70 dark:bg-amber-950/20 dark:border-amber-900/50 hover:bg-amber-100/80 dark:hover:bg-amber-900/30 shadow-sm"
          >
            <div className="col-span-1 text-center font-bold text-amber-700 dark:text-amber-400">
              D{i + 1}
            </div>

            <div className="col-span-5 flex items-center gap-2.5 min-w-0">
              {thumb ? (
                <img
                  src={thumb}
                  alt={d.title}
                  className="w-10 h-8 rounded-md object-cover shadow-sm flex-shrink-0"
                />
              ) : (
                <div className="w-10 h-8 rounded-md bg-amber-200/60 dark:bg-amber-900/50 flex items-center justify-center flex-shrink-0 text-amber-700">
                  <FileText size={14} />
                </div>
              )}
              <div className="min-w-0 flex-1">
                <div className="font-bold text-amber-900 dark:text-amber-200 truncate flex items-center gap-1.5">
                  <span>{d.title || "Untitled Exhibition Draft"}</span>
                  <span className="text-[10px] px-1.5 py-0.5 rounded bg-amber-200 dark:bg-amber-800 text-amber-800 dark:text-amber-200 font-semibold uppercase">
                    Draft
                  </span>
                </div>
              </div>
            </div>

            <div className="col-span-2">
              <span className="inline-block px-2 py-0.5 rounded text-[10px] font-bold bg-amber-200/80 dark:bg-amber-900/60 text-amber-900 dark:text-amber-200">
                {d.type || "App"}
              </span>
            </div>

            <div className="col-span-2 text-amber-800/80 dark:text-amber-300/80 flex items-center gap-1 text-[11px]">
              <Calendar size={12} className="opacity-60" />
              <span>{formatDate(d.savedAt || d.date)}</span>
            </div>

            <div className="col-span-2 flex justify-center gap-1">
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  onSelectDraft?.(d);
                }}
                className="px-2 py-1 text-[11px] font-semibold text-amber-900 bg-amber-200 rounded-md hover:bg-amber-300 transition flex items-center gap-1"
                title="Edit draft"
              >
                <Edit3 size={12} /> Edit
              </button>
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  handleDeleteDraft(d.id);
                }}
                className="p-1 text-red-600 hover:bg-red-100 rounded-md transition"
                title="Delete draft"
              >
                <Trash2 size={14} />
              </button>
            </div>
          </div>
        );
      })}

      {/* Published projects list */}
      {projects.length === 0 && drafts.length === 0 ? (
        <div className="text-gray-400 dark:text-gray-500 text-center py-12 italic text-sm">
          No exhibition projects on record. Click "Create Project" to curate one.
        </div>
      ) : (
        [...projects]
          .sort((a, b) => new Date(b.date || 0) - new Date(a.date || 0))
          .map((p, idx) => {
            const imgMatch = p.description?.match(/<img[^>]+src="([^">]+)"/);
            const thumb =
              p.coverImage ||
              (Array.isArray(p.images) && p.images.length > 0
                ? p.images[0]
                : imgMatch
                  ? imgMatch[1]
                  : null);
            const isSelected = selectedProjectId === p._id;

            return (
              <div
                key={p._id}
                onClick={() => onSelectProject?.(p._id)}
                className={`grid grid-cols-12 gap-3 p-2.5 items-center cursor-pointer rounded-xl text-xs transition-all border ${isSelected
                    ? "bg-[var(--primary)] text-white border-[var(--primary)] shadow-md"
                    : "bg-white dark:bg-gray-900 border-gray-100 dark:border-gray-800 hover:border-green-300 hover:bg-green-50/40 dark:hover:bg-gray-800/80 shadow-sm"
                  }`}
              >
                <div
                  className={`col-span-1 text-center font-bold ${isSelected ? "text-white" : "text-gray-400"
                    }`}
                >
                  {idx + 1}
                </div>

                <div className="col-span-5 flex items-center gap-2.5 min-w-0">
                  {thumb ? (
                    <img
                      src={thumb}
                      alt={p.title}
                      className="w-10 h-8 rounded-md object-cover shadow-sm flex-shrink-0"
                    />
                  ) : (
                    <div
                      className={`w-10 h-8 rounded-md flex items-center justify-center flex-shrink-0 ${isSelected
                          ? "bg-white/20 text-white"
                          : "bg-gray-100 dark:bg-gray-800 text-gray-400"
                        }`}
                    >
                      <ImageIcon size={14} />
                    </div>
                  )}

                  <div className="min-w-0 flex-1">
                    <div
                      className={`font-semibold truncate ${isSelected
                          ? "text-white"
                          : "text-gray-900 dark:text-gray-100"
                        }`}
                    >
                      {p.title}
                    </div>
                  </div>
                </div>

                <div className="col-span-2">
                  <span
                    className={`inline-block px-2 py-0.5 rounded text-[10px] font-bold ${
                      isSelected
                        ? "bg-white/25 text-white"
                        : "bg-green-100 dark:bg-green-950/60 text-green-800 dark:text-green-300"
                    }`}
                  >
                    {p.type || "App"}
                  </span>
                </div>

                <div
                  className={`col-span-2 flex items-center gap-1 text-[11px] ${isSelected
                      ? "text-white/80"
                      : "text-gray-500 dark:text-gray-400"
                    }`}
                >
                  <Calendar size={12} className="opacity-60" />
                  <span>{formatDate(p.date)}</span>
                </div>

                <div className="col-span-2 flex justify-center gap-1.5">
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      onSelectProject?.(p._id);
                    }}
                    className={`px-2.5 py-1 text-[11px] font-semibold rounded-md transition flex items-center gap-1 ${isSelected
                        ? "bg-white text-[var(--primary)] hover:bg-gray-100"
                        : "bg-[var(--primary)] text-white hover:opacity-90"
                      }`}
                    title="View & edit project"
                  >
                    <Edit3 size={12} /> Edit
                  </button>

                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      if (
                        window.confirm(
                          `Are you sure you want to delete project "${p.title}"?`
                        )
                      ) {
                        onDeleteProject?.(p._id);
                      }
                    }}
                    className={`p-1 rounded-md transition ${isSelected
                        ? "text-white hover:bg-white/20"
                        : "text-red-500 hover:bg-red-50 dark:hover:bg-red-950/40"
                      }`}
                    title="Delete project"
                  >
                    <Trash2 size={14} />
                  </button>
                </div>
              </div>
            );
          })
      )}
    </div>
  );
}
